import { ChangeEvent, InvalidEvent, useCallback, useEffect } from "react";
import { Form } from "react-router-dom";
import { Col, FormFeedback, FormGroup, FormText, Input, Label, Row } from "reactstrap";
import useValueList from "../../../hook/useValueList";
import { ContainerLayoutProps } from "../../../types/common";
import { Action } from "../../../types/reducer-action";
import { IValueInfo, IValueInfoInput } from "../../../types/valuelist";
import { parseCommonError, toNumber, trimToEmpty } from "../../../utils/commons";
import { DataEntryEvent } from "../../common/data-entry-reducer";
import LabelCol from "../../common/label-col";
import Textarea from "../../common/textarea/textarea";
import './style.scss';
import useParentValue from "./useParentValue";
import { ValueInfoForm, toValueInfoInput, useValueListDispatch, useValueListState } from "./value-list-manager-context";

const isDirty = (origin: IValueInfo, form: ValueInfoForm) => (
  trimToEmpty(origin.type) !== trimToEmpty(form.type)
  || trimToEmpty(origin.code) !== trimToEmpty(form.code)
  || trimToEmpty(origin.label) !== trimToEmpty(form.label)
  || trimToEmpty(origin.label2) !== trimToEmpty(form.label2)
  || trimToEmpty(origin.lang) !== trimToEmpty(form.lang)
  || toNumber(origin.level, 0) !== toNumber(form.level, 0)
  || trimToEmpty(origin.metadata) !== trimToEmpty(form.metadata)
  || trimToEmpty(origin.abbr) !== trimToEmpty(form.abbr)
  || trimToEmpty(origin.comment) !== trimToEmpty(form.comment)
  || trimToEmpty(origin.desc) !== trimToEmpty(form.desc)
  || origin.active !== form.active
  || toNumber(origin.displayOrder, 0) !== toNumber(form.displayOrder, 0)
  || toNumber(origin.parent?.id, 0) !== toNumber(form.parentId, 0)
);

interface ValueEditFormProps extends ContainerLayoutProps {
  id?: string;
  onChange?: (changed: boolean) => void;
  onSubmit?: (input: IValueInfoInput) => void;
}

const ValueEditForm = (props: ValueEditFormProps) => {
  const { id, wide, onChange, onSubmit, autoFocus } = props;

  const { dataDetails, form, errors } = useValueListState();

  const dispatch = useValueListDispatch();

  const isNew = !form.id;

  const colWidth = wide ? 6 : 12;

  const [valueList] = useValueList(form.type);

  const [parentType, parentValueList, defaultParentId] = useParentValue(form.type);

  useEffect(() => {
    if (!form.parentId || form.parentId <= 0) {
      dispatch(new Action(DataEntryEvent.UpdateForm, {
        parentId: defaultParentId ?? 0
      }));
    }
  }, [form, defaultParentId, dispatch]);

  useEffect(() => {
    dataDetails && onChange?.(isDirty(dataDetails, form));
  }, [dataDetails, form, onChange]);

  const handleInvalid = (event: InvalidEvent<HTMLInputElement>) => {
    event.preventDefault();

    const { name, value, validity } = event.target;

    let msg = parseCommonError(event);
    if (name === 'code') {
      if (value.startsWith('_') || value.endsWith('_')) {
        msg = 'Code cannot begin or end with an underscore.'
      } else if (value.includes('__')) {
        msg = 'Code cannot contain consecutive underscores.';
      } else {
        msg = 'Invalid code format.';
      }
    } else if (name === 'level') {
      if (validity.rangeOverflow) {
        msg = 'Please enter a valid number between 1 and 9.';
      } else if (validity.badInput) {
        msg = 'Please enter a number.';
      } else if (validity.stepMismatch) {
        msg = 'Please enter an integer value.';
      }
    }

    dispatch(new Action(DataEntryEvent.AddErrors, {
      [name]: msg
    }));
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    dispatch(new Action(DataEntryEvent.UpdateForm, {
      [name]: (name === 'code' ? value.toUpperCase() : value)
    }));
  };

  const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isNew && valueList.some(v => v.code === form.code)) {
      dispatch(new Action(DataEntryEvent.AddErrors, {
        code: 'Code already exists.'
      }));

      return;
    }

    const input = toValueInfoInput(form);
    if (input) {
      input.level = +form.level;
      onSubmit?.(input);
    }
  }, [form]);

  const handleReset = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    dispatch(new Action(DataEntryEvent.ResetForm))
  };

  return (
    <Form id={id} onSubmit={handleSubmit} onReset={handleReset}>
      <Row className="gx-5">
        <Col md={colWidth}>
          <FormGroup row>
            <LabelCol sm={4}>Group Type:</LabelCol>
            <LabelCol sm={8}>{form.type}</LabelCol>
          </FormGroup>
        </Col>
        <Col md={colWidth}>
          <FormGroup row>
            {isNew ? <>
              <Label for="code" sm={4}>Value Code: (required)</Label>
              <Col sm={8}>
                <Input 
                  id="code" 
                  name="code" 
                  placeholder="Enter a code"
                  maxLength={20}
                  value={form.code ?? ''} 
                  onChange={handleChange}
                  pattern="^[A-Za-z0-9]+(?:_[A-Za-z0-9]+)*$"
                  invalid={!!errors.code} 
                  onInvalid={handleInvalid}
                  autoFocus={autoFocus}
                  required />
                <FormText>The value can only contain alphanumeric characters and underscores.</FormText>
                <FormFeedback>{errors.code}</FormFeedback>
              </Col>
            </> : <>
              <LabelCol sm={4}>Value Code:</LabelCol>
              <LabelCol sm={8}>{form.code}</LabelCol>
            </>}
          </FormGroup>
        </Col>
      </Row>

      <Row className="gx-5">
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="label" sm={4}>Value Name: (required)</Label>
            <Col sm={8}>
              <Input id="label" name="label"
                placeholder="Enter a value name" maxLength={100}
                value={form.label ?? ''} onChange={handleChange}
                invalid={!!errors.label} onInvalid={handleInvalid}
                required />
              <FormFeedback>{errors.label}</FormFeedback>
            </Col>
          </FormGroup>
        </Col>
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="label2" sm={4}>Alternative Value Name:</Label>
            <Col sm={8}>
              <Input id="label2" name="label2"
                placeholder="Enter an alternative value name" maxLength={100}
                value={form.label2 ?? ''} onChange={handleChange} />
            </Col>
          </FormGroup>
        </Col>
      </Row>

      {parentType &&
        <Row className="gx-5">
          <Col md={colWidth}>
            <FormGroup row>
              <LabelCol sm={4}>Parent Type:</LabelCol>
              <LabelCol sm={8}>{parentType}</LabelCol>
            </FormGroup>
          </Col>
          <Col md={colWidth}>
            <FormGroup row>
              <Label for="parent_code" sm={4}>Parent Code: (required)</Label>
              <Col sm={8}>
                <Input type="select" id="parent_code" name="parentId"
                  value={form.parentId || ''} onChange={handleChange}
                  invalid={!!errors.parentId} onInvalid={handleInvalid}
                  required>
                  {parentValueList?.length === 0 && <option disabled={true}>Loading</option>}
                  {parentValueList && parentValueList.length > 0 && !form.parentId &&
                    <option value="">Select a code</option>
                  }
                  {parentValueList?.map(parent =>
                    <option key={parent.id} value={parent.id} title={parent.label}>{parent.code}</option>
                  )}
                </Input>
                <FormFeedback>{errors.parentId}</FormFeedback>
              </Col>
            </FormGroup>
          </Col>
        </Row>
      }

      <Row className="gx-5">
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="language" sm={4}>Language:</Label>
            <Col sm={8}>
              <Input type="select" id="language" name="lang"
                value={form.lang ?? ''} onChange={handleChange}>
                <option value="">Select a language</option>
                <option value="EN">English</option>
                <option value="FR">French</option>
              </Input>
            </Col>
          </FormGroup>
        </Col>
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="level" sm={4}>Level:</Label>
            <Col sm={8}>
              <Input type="number" id="level" name="level" placeholder="Enter a level"
                value={form.level ?? ''} onChange={handleChange}
                invalid={!!errors.level} onInvalid={handleInvalid}
                min={1} max={9} />
              <FormFeedback>{errors.level}</FormFeedback>
            </Col>
          </FormGroup>
        </Col>
      </Row>

      <Row className="gx-5">
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="metadata" sm={4}>Metadata:</Label>
            <Col sm={8}>
              <Input id="metadata" name="metadata" placeholder="Enter metadata" maxLength={100}
                value={form.metadata ?? ''} onChange={handleChange} />
            </Col>
          </FormGroup>
        </Col>
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="abbr" sm={4}>Abbreviation:</Label>
            <Col sm={8}>
              <Input id="abbr" name="abbr" placeholder="Enter an abbreviation" maxLength={30}
                value={form.abbr ?? ''} onChange={handleChange} />
            </Col>
          </FormGroup>
        </Col>
      </Row>

      <FormGroup row>
        <Label for="comment" sm={wide ? 2 : 4}>On-screen Comment:</Label>
        <Col sm={wide ? 10 : 8} className={wide ? 'ps-2' : ''}>
          <Textarea id="comment" name="comment"
            placeholder="Enter a comment to display"
            rows={3} maxLength={255}
            value={form.comment ?? ''}
            onChange={handleChange}
            onInvalid={handleInvalid} />
        </Col>
      </FormGroup>

      <FormGroup row>
        <Label for="description" sm={wide ? 2 : 4}>Value Description:</Label>
        <Col sm={wide ? 10 : 8} className={wide ? 'ps-2' : ''}>
          <Input type="textarea" id="description" name="desc"
            placeholder="Enter a description for the value"
            rows={3}
            value={form.desc ?? ''}
            onChange={handleChange}
            onInvalid={handleInvalid} />
        </Col>
      </FormGroup>

      <FormGroup check>
        <Input id="active" type="checkbox" checked={!!form.active}
          onChange={() => dispatch(new Action(DataEntryEvent.UpdateForm, { active: !form.active }))} />
        {' '}
        <Label for="active" check>Active?</Label>
      </FormGroup>
    </Form>
  );
};

export default ValueEditForm;
